import * as R from 'ramda';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { usePreviousLocation } from '../../../hooks/usePreviousLocation';
import storage from '../../../hooks/useStorage/useStorage';
import { useConfigureApm } from '../../../lib/apm/hooks/useConfigueApm';
import PATHS from '../../../routes/routes.const';
import api from '../../../services/api';
import { useFindBrandGeneralSettings } from '../../../services/modules/brandGeneralSettings/brandGeneralSettings.queries';
import { useGetBrandContext } from '../../../services/modules/context/context.queries';
import { getBrandContext } from '../../../services/modules/context/context.repository';
import { useGetAbilities, useGetTeam } from '../../../services/modules/users/users.queries';
import { getAbilities, getTeam } from '../../../services/modules/users/users.repository';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../../stores/ProductDetails/productDetails.store';
import useSystemParametersStore from '../../../stores/SystemParameters/useSystemParameters.store';
import { redirectAllTabsToRoot } from '../../../utils/resetApp.util';
import { recordBrandVisit } from '../../home/store-home/connections/recordBrandVisit';
import { IntegrationConfigName } from '../../integrations/integrations.types';
import { useAuthContext } from '../../login/AuthContext';
import { useHandleLogoutWhenOfflineWithoutEncryptionKey } from '../../offline/hooks/useHandleLogoutWhenOfflineWithoutEncryptionKey';
import { useHandleOfflineEncryptionKeys } from '../../offline/hooks/useHandleOfflineEncryptionKeys/useHandleOfflineEncryptionKeys';
import { useAbility } from '../../users/AbilityContext';
import { loggedAsBrand, loggedAsStore } from '../contextUtils';
import { StorageEnum } from '../storage';
import {
  ApplicationContextProps,
  ApplicationContextProviderProps,
  Brand,
  Team,
} from './applicationContext.types';
import { isAdminTeceo } from './applicationContext.utils';
import useAppEnvironment from './hooks/useAppEnvironment/useAppEnvironment';
import {
  applicationContextReducer,
  applicationContextStateInitialState,
} from './reducer/applicationContextReducer';
import {
  loadContext,
  loadingBrands,
  setAdminTeceo,
  setBrand,
  setUser,
} from './reducer/applicationContextReducer.actions';

export const ApplicationContext = createContext<ApplicationContextProps>({
  state: applicationContextStateInitialState,
  team: undefined,
  userRole: 'COMPRADOR',
  dispatch: () => {
    return;
  },
  changeTeam: () => undefined,
  changeTeamInOffline: () => undefined,
  changeBrand: () => undefined,
  removeSelectedBrand: () => undefined,
  loadTeam: async () => undefined,
  loadBrands: async () => undefined,
  isAdminBrand: false,
  isAdminStore: false,
  isBuyer: false,
  isSeller: false,
  isBrand: false,
  isStore: false,
  hasIntegrations: false,
  hasVerupIntegration: false,
  hasBrandIntegratorIntegration: false,
  hasLinxIntegration: false,
  hasEmillenniumIntegration: false,
  hasTotvsIntegration: false,
  hasHopeIntegration: false,
  hasCustomerIntegration: false,
  canForceReverseOrderSync: false,
  brandId: undefined,
  showContactBrandButton: false,
  hasOrderIntegration: true,
  isEnabledHideOrderValue: false,
  previousLocation: undefined,
  isCustomRole: false,
  hasPaymentGateway: false,
  brandPaymentGateways: [],
  hasAutoImportAccountsReceivable: false,
});

export const useApplicationContext = () => useContext<ApplicationContextProps>(ApplicationContext);

export const ApplicationContextProvider = ({
  children,
  isOfflineMode,
}: ApplicationContextProviderProps) => {
  const [state, dispatch] = useReducer(applicationContextReducer, undefined, () => ({
    ...applicationContextStateInitialState,
    isAdminTeceo: isAdminTeceo(),
  }));
  const updateGeneralSettings = useGeneralSettingsStore((state) => state.updateGeneralSettings);
  const clearGeneralSettings = useGeneralSettingsStore((state) => state.clearGeneralSettings);

  const previousLocation = usePreviousLocation();

  const user = storage.getUser();
  const storageTeamId = user[StorageEnum.TEAM_ID];

  const { data: offlineCachedTeam } = useGetTeam({
    enabled: false,
    teamId: storageTeamId,
  });
  const { data: offlineCachedAbilities } = useGetAbilities({
    enabled: false,
    teamId: storageTeamId,
  });
  const { data: offlineCachedContext } = useGetBrandContext({
    enabled: false,
    teamId: storageTeamId,
  });
  const { data: offlineCachedGeneralSettings } = useFindBrandGeneralSettings({
    options: { enabled: false, teamId: storageTeamId },
  });

  const { applyEnvironment } = useAppEnvironment();
  const { logout, loginContext } = useAuthContext();
  const { getAbilities: getAbilitiesInContext, resetAbilities } = useAbility();
  const { resetStore, selectedOrder } = useOrderBookStore(
    useShallow((bookState) => ({
      resetStore: bookState.resetStore,
      selectedOrder: bookState.selectedOrder,
    })),
  );

  useConfigureApm({
    userData: state.user
      ? { name: state.user.name, email: state.user.email, teamId: state.user.id }
      : undefined,
  });
  useHandleOfflineEncryptionKeys({
    teamId: state.user?.id,
  });
  useHandleLogoutWhenOfflineWithoutEncryptionKey({
    logout,
    teamId: state.user?.id,
  });

  const resetProductActionsStore = useProductActionsStore(
    (productActionsState) => productActionsState.resetProductActionsStore,
  );
  const resetProductDetailsFilterWithOrders = useProductDetailsStore(
    (productDetailsState) => productDetailsState.resetFilterWithOrders,
  );
  const clearBrandSystemParameters = useSystemParametersStore(
    (parametersState) => parametersState.clearBrandSystemParameters,
  );

  const isBrand = loggedAsBrand(state.user?.role) === true || !!state.user?.isCustomRole;
  const isStore = loggedAsStore(state.user?.role) === true;
  const history = useHistory();

  useEffect(() => {
    storage.setLastLoginWasFromBrand(isBrand);
  }, [isBrand]);

  useEffect(() => {
    api.setBrandContext(state.brand?.id);
  }, [state.brand?.id]);

  const loadTeam = useCallback(async () => {
    const user = storage.getUser();

    if (!user[StorageEnum.TEAM_ID] || !user[StorageEnum.SESSION_ID]) {
      api.logout();
    }

    const onlineTeamResponse = isOfflineMode ? undefined : await getTeam();
    const team = onlineTeamResponse?.data ?? offlineCachedTeam;

    if (!team) {
      return;
    }

    if (team.role === 'ADMIN') {
      dispatch(setAdminTeceo());

      const personaTeamResponse: any = await api.getTeamById(user[StorageEnum.TEAM_ID]);

      if (personaTeamResponse.ok) {
        dispatch(setUser(personaTeamResponse.data));
      }
    } else {
      dispatch(setUser(team));
    }
  }, []);

  const loadInitialContext = useCallback(async () => {
    if (R.isNil(localStorage.user)) {
      logout();
      return;
    }

    const savedBrandId = storage.getBrandId();
    if (savedBrandId) {
      api.setBrandContext(savedBrandId);
    }

    const response = isOfflineMode ? offlineCachedAbilities : await getAbilities();

    if (!response && isOfflineMode) {
      history.push(PATHS.offline.loggedNoData.path);
      return;
    }

    if (!response || (!response.ok && response.status !== 401)) {
      history.push(PATHS.public.serverError.path, { lastPathname: history.location.pathname });
      return;
    }

    if (!response.ok && response.status === 400) {
      logout();
      return;
    }

    if (!R.isEmpty(response.data)) {
      await loadTeam();
    }
  }, [history, loadTeam, logout]);

  const updateLocalStorageUser = (newProps: any) => {
    if (!localStorage.user) {
      return;
    }

    const localStorageUser = JSON.parse(localStorage.user);
    localStorage.setItem('user', JSON.stringify({ ...localStorageUser, ...newProps }));
  };

  const changeTeam = useCallback(
    (team: Team) => {
      applyEnvironment(team.role, team.brand?.id);
      resetAbilities();
      updateLocalStorageUser({
        [StorageEnum.TEAM_ID]: team.id,
        [StorageEnum.BRAND_ID]: undefined,
        [StorageEnum.SIDEBAR_STATE]: undefined,
      });
      api.setTeam(team.id);
      dispatch(setUser(team));
      dispatch(setBrand(undefined));
      api.setBrandContext(undefined);
      history.replace('/');
      resetStore();
      resetProductDetailsFilterWithOrders();
      resetProductActionsStore();
      getAbilitiesInContext();
      // Notify other tabs to redirect to root to ensure fresh context
      redirectAllTabsToRoot();
    },
    [history, getAbilitiesInContext, applyEnvironment],
  );

  const changeTeamInOffline = (team: Team) => {
    updateLocalStorageUser({
      [StorageEnum.TEAM_ID]: team.id,
      [StorageEnum.BRAND_ID]: undefined,
      [StorageEnum.SIDEBAR_STATE]: undefined,
    });
    location.reload();
  };

  const applyBrand = useCallback(
    (brand: Brand) => {
      updateLocalStorageUser({ [StorageEnum.BRAND_ID]: brand.id });
      const sessionId = state.user?.id || 'anonymous';
      const isSameBrand = selectedOrder?.brand?.id === brand.id;
      storage.removeSelectedOrder(sessionId);
      if (!isSameBrand) {
        resetStore(state.user);
      }
      resetProductDetailsFilterWithOrders();
      api.setBrandContext(brand.id);
      dispatch(setBrand(brand));
    },
    [resetStore, state.user],
  );

  const changeBrand = useCallback(
    (brand: Brand) => {
      applyBrand(brand);

      if (isStore) {
        void recordBrandVisit(state.user?.id, brand.id);
      }
    },
    [applyBrand, isStore, state.user?.id],
  );

  const removeSelectedBrand = useCallback(() => {
    updateLocalStorageUser({ [StorageEnum.BRAND_ID]: undefined });
    const sessionId = state.user?.id || 'anonymous';
    storage.removeSelectedOrder(sessionId);
    api.setBrandContext();
    api.setBrandContextInSession(undefined);
    clearBrandSystemParameters();
    clearGeneralSettings();
    dispatch(setBrand(undefined));
  }, [state.user, clearBrandSystemParameters, clearGeneralSettings]);

  const loadBrands = useCallback(async () => {
    dispatch(loadingBrands());
    const brandsResponse = isOfflineMode ? undefined : await getBrandContext();
    const brandsFromResponse = brandsResponse?.data ?? offlineCachedContext;
    const brands = brandsFromResponse || [];
    dispatch(loadContext({ brands }));
  }, []);

  useEffect(() => {
    if (offlineCachedGeneralSettings && isOfflineMode) {
      updateGeneralSettings(offlineCachedGeneralSettings);
    }
  }, [offlineCachedGeneralSettings]);

  useEffect(() => {
    loadInitialContext();
  }, [loadInitialContext, state.brands]);

  useEffect(() => {
    if (state.user?.id) {
      loadBrands();
    }
  }, [loadBrands, state.user?.id]);

  useEffect(() => {
    const brand = state.brands.find((item) => item.id === loginContext?.id);

    if (brand) {
      applyBrand(brand);
    }
  }, [applyBrand, loginContext?.id, state.brands]);

  useEffect(() => {
    if (isBrand) {
      const brandId = state.user?.brand?.id;
      updateLocalStorageUser({ [StorageEnum.BRAND_ID]: brandId });
      api.setBrandContext(brandId);
      dispatch(setBrand(undefined));
      return;
    }

    if (!R.isNil(state.brands) && !R.isEmpty(state.brands)) {
      const localStorageUser = storage.getUser();
      const brandId = localStorageUser[StorageEnum.BRAND_ID];
      if (brandId && state.brands) {
        const brand = state.brands.find(({ id }) => id === brandId);
        if (brand) {
          applyBrand(brand);
        }
      }
    }
  }, [applyBrand, isBrand, state.brands, state.user?.brand?.id]);

  const isAdminBrand = state.user?.role === 'ADMIN_MARCA';
  const isAdminStore = state.user?.role === 'ADMIN_LOJA';
  const isSeller = state.user?.role === 'VENDEDOR';
  const isBuyer = state.user?.role === 'COMPRADOR';

  const hasVerupIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.INTEGRADOR_VERUP_CSV,
  );

  const hasLinxIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.INTEGRADOR_LYNX_SERVER,
  );

  const hasEmillenniumIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.INTEGRADOR_EMILLENNIUM,
  );

  const hasTotvsIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.INTEGRADOR_TOTVS_MODA,
  );

  const hasBrandIntegratorIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.TECEO_BRAND_INTEGRATOR,
  );

  const hasHopeIntegration = !!state.user?.brand?.integrations?.find(
    (integration) =>
      integration.active && integration.name === IntegrationConfigName.INTEGRADOR_HOPE,
  );

  const activeErpIntegration = state.user?.brand?.integrations?.find(
    (integration) => integration.active,
  );

  const hasCustomerIntegration = !!state.user?.brand?.integrations?.some(
    (integration) => integration.active && integration.hasCustomerIntegration,
  );

  const hasOrderIntegration = !!state.user?.brand?.integrations?.some(
    (integration) => integration.active && integration.hasOrderIntegration,
  );

  const canForceReverseOrderSync = !!state.user?.brand?.integrations?.some(
    (integration) => integration.active && integration.canForceReverseOrderSync,
  );

  const hasAutoImportAccountsReceivable = !!state.user?.brand?.integrations?.some(
    (integration) => integration.active && integration.hasAutoImportAccountsReceivable,
  );

  const integrations = state.user?.brand?.integrations;

  const brandSlug = state.brand?.slug || state.user?.brand?.slug;
  const brandId = state.brand?.id || state.user?.brand?.id;
  const showContactBrandButton =
    !state.hideContactBrandButton && isStore && !!state.brand?.customerSupportPhone;

  const isEnabledHideOrderValue = useMemo(
    () => isBrand && !isOfflineMode,
    [isBrand, isOfflineMode],
  );

  const brandPaymentGateways = state.user?.brand?.paymentGateways || [];
  const hasPaymentGateway = (state.user?.brand?.paymentGateways?.length ?? 0) > 0;

  const team = state.user;

  useEffect(() => {
    if (!team) {
      return;
    }
    applyEnvironment(team.role, brandId);
  }, [team, brandId, applyEnvironment]);

  const props: ApplicationContextProps = {
    state,
    brandSlug,
    isAdminStore,
    isAdminBrand,
    isSeller,
    isBuyer,
    isBrand,
    isCustomRole: !!state.user?.isCustomRole,
    isStore,
    brandId,
    hasBrandIntegratorIntegration,
    hasVerupIntegration,
    hasLinxIntegration,
    hasEmillenniumIntegration,
    hasTotvsIntegration,
    hasHopeIntegration,
    hasIntegrations: !!activeErpIntegration,
    integrations,
    team,
    userRole: state.user?.role || 'COMPRADOR',
    dispatch,
    changeTeam,
    changeTeamInOffline,
    changeBrand,
    removeSelectedBrand,
    loadTeam,
    loadBrands,
    hasCustomerIntegration,
    canForceReverseOrderSync,
    showContactBrandButton,
    hasOrderIntegration,
    isEnabledHideOrderValue,
    previousLocation,
    hasPaymentGateway,
    erpIntegration: activeErpIntegration,
    brandPaymentGateways,
    hasAutoImportAccountsReceivable,
  };

  return <ApplicationContext.Provider value={props}>{children}</ApplicationContext.Provider>;
};
